import React from 'react'

/** Границы ползунка; off — значение, выключающее параметр, offLabel — его подпись. */
export interface SliderRange {
  min: number
  max: number
  step: number
  off?: number | null
  offLabel?: string | null
}

/** Ширина подписи границы ползунка (min / max), px. */
export const SLIDER_BOUND_WIDTH = 40
/** Ширина дорожки ползунка, px — одинакова для любых границ. */
export const SLIDER_TRACK_WIDTH = 220
/** Ширина всего блока ползунка: две подписи, дорожка и два зазора по 8px. */
export const SLIDER_BLOCK_WIDTH = SLIDER_BOUND_WIDTH * 2 + SLIDER_TRACK_WIDTH + 16
/** Ширина бегунка form-range, px: центр бегунка ходит от half до track − half. */
const THUMB_WIDTH = 16

interface Props {
  range: SliderRange
  value: number
  onChange: (value: number) => void
  disabled?: boolean
  /** Рекомендуемое значение: метка на шкале в его позиции. */
  defaultValue?: number | null
}

/** Подпись значения ползунка: у значения, выключающего параметр, — ещё и его название. */
export const sliderValueLabel = (range: SliderRange, value: number): string =>
  range.off != null && value === range.off && range.offLabel
    ? `${value} — ${range.offLabel}`
    : String(value)

// Every slider has the same geometry whatever its bounds: fixed-width bound labels around a
// fixed-width track, the value on its own line below. The default sits as a tick above the track.
export const RangeSlider: React.FC<Props> = ({
  range,
  value,
  onChange,
  disabled,
  defaultValue,
}) => {
  const span = range.max - range.min
  const showMark =
    defaultValue != null && span > 0 && defaultValue >= range.min && defaultValue <= range.max
  const markLeft = showMark
    ? THUMB_WIDTH / 2 + ((defaultValue! - range.min) / span) * (SLIDER_TRACK_WIDTH - THUMB_WIDTH)
    : 0

  return (
    <div className='d-flex flex-column' style={{ width: SLIDER_BLOCK_WIDTH }}>
      <div className='d-flex align-items-center gap-2'>
        <span className='text-muted fs-8 text-end' style={{ width: SLIDER_BOUND_WIDTH }}>
          {range.min}
        </span>
        <div className='position-relative' style={{ width: SLIDER_TRACK_WIDTH }}>
          {showMark && (
            <span
              data-testid='slider-default-mark'
              title={`По умолчанию: ${defaultValue}`}
              className='position-absolute bg-gray-400'
              style={{ left: markLeft - 1, top: -2, width: 2, height: 8, borderRadius: 1 }}
            />
          )}
          <input
            type='range'
            className='form-range'
            style={{ width: SLIDER_TRACK_WIDTH }}
            min={range.min}
            max={range.max}
            step={range.step}
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value))}
          />
        </div>
        <span className='text-muted fs-8' style={{ width: SLIDER_BOUND_WIDTH }}>
          {range.max}
        </span>
      </div>
      <span className='fw-semibold fs-7 text-center text-truncate'>
        {sliderValueLabel(range, value)}
      </span>
    </div>
  )
}

/** Рекомендуемое значение бледным текстом под полем. Ничего не рисует, если значения нет. */
export const DefaultHint: React.FC<{ text?: string | null }> = ({ text }) =>
  text == null || text === '' ? null : (
    <span className='text-gray-500 fs-8'>По умолчанию: {text}</span>
  )
